import { useState } from 'react'
import { router } from '@inertiajs/react'
import { AlertTriangle, X, Loader2 } from 'lucide-react'

interface DeleteConfirmModalProps {
  isOpen: boolean
  onClose: () => void
  title: string
  message: string
  deleteUrl: string
  itemName: string
  redirectUrl?: string
}

export default function DeleteConfirmModal({
  isOpen,
  onClose,
  title,
  message,
  deleteUrl,
  itemName,
  redirectUrl
}: DeleteConfirmModalProps) {
  const [isDeleting, setIsDeleting] = useState(false)
  const [confirmText, setConfirmText] = useState('')

  if (!isOpen) return null

  const handleDelete = () => {
    setIsDeleting(true)
    router.delete(deleteUrl, {
      onSuccess: () => {
        setIsDeleting(false)
        onClose()
        if (redirectUrl) {
          router.visit(redirectUrl)
        }
      },
      onError: () => {
        setIsDeleting(false)
      }
    })
  }

  const isConfirmValid = confirmText.toLowerCase() === 'delete'

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-xl max-w-md w-full mx-4 p-6">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-chestnut-charcoal/60 hover:text-chestnut-charcoal"
        >
          <X className="h-5 w-5" />
        </button>

        <div className="flex items-center gap-4 mb-4">
          <div className="p-3 bg-red-100 rounded-full">
            <AlertTriangle className="h-6 w-6 text-red-600" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-chestnut-charcoal">{title}</h3>
          </div>
        </div>

        <p className="text-chestnut-charcoal/70 mb-4">{message}</p>

        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
          <p className="text-sm text-red-800 mb-2">
            You are about to permanently delete: <strong>{itemName}</strong>
          </p>
          <p className="text-sm text-red-700">This action cannot be undone.</p>
        </div>

        <div className="mb-6">
          <label className="block text-sm font-medium text-chestnut-charcoal mb-2">
            Type <span className="font-mono text-red-600">delete</span> to confirm
          </label>
          <input
            type="text"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder="delete"
            className="w-full px-4 py-2 border border-chestnut-charcoal/20 rounded-lg focus:ring-2 focus:ring-red-500/20 focus:border-red-500"
          />
        </div>

        <div className="flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-chestnut-charcoal/70 hover:text-chestnut-charcoal font-medium"
            disabled={isDeleting}
          >
            Cancel
          </button>
          <button
            onClick={handleDelete}
            disabled={!isConfirmValid || isDeleting}
            className="px-4 py-2 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {isDeleting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Deleting...
              </>
            ) : (
              'Delete Permanently'
            )}
          </button>
        </div>
      </div>
    </div>
  )
}
